import { mcn } from "@/utils/mergeClassNames";
import { useState } from "react";
import Label, { LabelProps } from "../Label";
import Dropdown from "./Dropdown";

type DatePickerProps = {
  id?: string;
  className?: string;
  labelProps?: LabelProps;
  onChange: (date?: Date) => void;
  disabled?: boolean;
  date?: Date;
  showFutureYears: boolean;
  isMandatory?: boolean;
};

function DatePicker(props: DatePickerProps) {
  const [selectedDate, setSelectedDate] = useState(props.date ?? new Date());

  const disabled: boolean = props.disabled ?? false;

  const getDaysInMonth = (year: number, monthIndex: number) =>
    new Date(year, monthIndex + 1, 0).getDate();

  const buildStableDate = (year: number, monthIndex: number, day: number) =>
    new Date(year, monthIndex, day, 12, 0, 0, 0); // noon helps avoid timezone midnight shifts

  const clampDay = (year: number, monthIndex: number, day: number) =>
    Math.min(day, getDaysInMonth(year, monthIndex));

  const currentYear = new Date().getFullYear();
  const yearLength = props.showFutureYears ? 120 : 100;
  const years = Array.from({ length: yearLength }, (_, i) => currentYear - 100 + i).reverse();
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const selectedYear = selectedDate.getFullYear();
  const selectedMonthIndex = selectedDate.getMonth();
  const daysInMonth = getDaysInMonth(selectedYear, selectedMonthIndex);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const handleYearChange = (year?: number) => {
    if (year == null) return;
    const day = clampDay(year, selectedMonthIndex, selectedDate.getDate());
    const newDate = buildStableDate(year, selectedMonthIndex, day);
    setSelectedDate(newDate);
    props.onChange(newDate);
  };

  const handleMonthChange = (month?: number) => {
    if (month == null) return;
    const monthIndex = month - 1;
    const day = clampDay(selectedYear, monthIndex, selectedDate.getDate());
    const newDate = buildStableDate(selectedYear, monthIndex, day);
    setSelectedDate(newDate);
    props.onChange(newDate);
  };

  const handleDayChange = (day?: number) => {
    if (day == null) return;
    const safeDay = clampDay(selectedYear, selectedMonthIndex, day);
    const newDate = buildStableDate(selectedYear, selectedMonthIndex, safeDay);
    setSelectedDate(newDate);
    props.onChange(newDate);
  };
  return (
    <div className={mcn("relative", props.className)}>
      {props.labelProps && (
        <Label
          {...props.labelProps}
          className={mcn(props.labelProps.className, "mb-1")}
          isMandatory={props.isMandatory}
        />
      )}

      <div className="grid grid-cols-3">
        <Dropdown
          className="col-span-1 tablet:col-span-1"
          onChange={(item) => handleYearChange(item?.data)}
          items={years.map((year) => ({
            text: year.toString(),
            id: year,
            data: year,
            preSelected:
              props.date !== null && selectedDate.getFullYear() === year
                ? true
                : false,
          }))}
          disabled={disabled}
          labelProps={{
            text: "Year",
            className: "sr-only",
          }}
        />
        <Dropdown
          className="ml-sm col-span-1 tablet:col-span-1"
          onChange={(item) => handleMonthChange(item?.data)}
          items={months.map((month) => ({
            text: month.toString(),
            id: month,
            data: month,
            preSelected:
              props.date !== null && selectedDate.getMonth() + 1 === month
                ? true
                : false,
          }))}
          disabled={disabled}
          labelProps={{
            text: "Month",
            className: "sr-only",
          }}
        />
        <Dropdown
          className="ml-sm col-span-1 tablet:col-span-1"
          onChange={(item) => handleDayChange(item?.data)}
          items={days.map((day) => ({
            text: day.toString(),
            id: day,
            data: day,
            preSelected:
              props.date !== null && selectedDate.getDate() === day
                ? true
                : false,
          }))}
          disabled={disabled}
          labelProps={{
            text: "Day",
            className: "sr-only",
          }}
        />
      </div>
    </div>
  );
}

export default DatePicker;
